import { planTypes } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { translation } from '@/plugins/i18n';
import { logger } from '@/utils/log';
import { dataLayerPayload4Rebranding } from '@zoom/billing-rebranding';
import store from '@/store';

function getReferrer() {
  const url = window?.newBillingConfig?.referrer;
  const referrerRegExp = {
    'billing': url => /\/billing/.test(url),
    'pricing': url => /\/pricing/.test(url),
  };
  return Object.keys(referrerRegExp).find(k => {
    return referrerRegExp[k](url);
  });
}

const referrer = getReferrer() || '';

const gaFlowTypes = {
  buyNow: 'Direct Purchase',
  basic2pro: 'Free to Paid Upgrade',
  edit: `${referrer} edit`.trim(),
  add2cart: `${referrer} new add-ons`.trim(),
  pro2biz: 'billing upgrade paid2paid',
  [flowTypes.pro2prop]: 'billing upgrade paid2paid',
  [flowTypes.pro2bundle]: 'billing upgrade paid2paid',
  [flowTypes.prop2bundle]: 'billing upgrade paid2paid',
  [flowTypes.biz2bundle]: 'billing upgrade paid2paid',
  addOnPurchase: 'Add-On Purchase',
  invoiceNoAutopay: 'Invoice No Autopay',
  invoiceWAutopay: 'Invoice with Autopay',
  trial2paid: `free trial upgrade${referrer ? ` - ${referrer} new add-ons`: ''}`,
  // BUY TRIAL - url type=buy_trial
  [flowTypes.buyTrial]: `buy free trial`,
  // FREE TRIAL - url type=add_trial
  [flowTypes.add2trial]: `add free trial${referrer ? ` - ${referrer} new add-ons`: ''}`,
  [flowTypes.zs2ze]: `upsell`,
  [flowTypes.switch]: 'cancellation - save offer'
}
export const TRACKING_ARL_TEXT = {
  ARL: 'arl',
  NON_ARL: 'non-arl',
};

export function getARlTagText() {
  const { basicConfig } = store.getters;
  const { isARLCustomer } = basicConfig;
  return `${isARLCustomer ? TRACKING_ARL_TEXT.ARL : TRACKING_ARL_TEXT.NON_ARL}`;
}
export const getTrackingFlowType = () => {
  if(window.$context.is.edit && window.$context.is.delta) return 'add license';
  if(window.$context.is.winback) return 'Past purchase winback'
  if (window.$context.is.billingBasicPlusOffer) return `cancellation ${getARlTagText()} - switch to basic plus`;
  if (window.$context.is.billingGetOffer) return `cancellation ${getARlTagText()} - save offer`;
  return gaFlowTypes[window.$context?.flow];
}

const CHARGE_AMOUNT_DISPLAY = {
  PRORATED: 'prorated',
  NORMAL: 'normal',
}

// customizable raw payload
const EventTracking = (arg) => {
  let payload = arg;
  if (typeof arg === 'function') {
    // use a modifier to reuse the payload
    const basicPayload = {
      flowType: getTrackingFlowType(),
      currencyCode: window.$context?.currency,
    };
    payload = Object.assign(basicPayload, arg(basicPayload));
  }
  payload = dataLayerPayload4Rebranding?.(payload) ?? payload;
  payload = Object.assign(payload, {
    chargeAmountDisplay: window.__$app__?.$store.getters.isProrated ? CHARGE_AMOUNT_DISPLAY.PRORATED : CHARGE_AMOUNT_DISPLAY.NORMAL,
  })
  logger.table(payload);
  Array.isArray(window.dataLayer) && window.dataLayer.push(payload);
};

const basicFormatter = (...args) => {
  const [event, buyInteraction, buySelection, flowType = getTrackingFlowType()] = args;
  EventTracking({
    event,
    buyInteraction,
    buySelection,
    flowType
  });
};

EventTracking.buyInteraction = (buyInteraction, buySelection, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType,
});

EventTracking.buyInteractionWithBizInfo = (buyInteraction, buySelection,  businessPersonalAccount,
  businessSize,
  businessRegion, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType,
  ...(businessPersonalAccount && { businessPersonalAccount }),
  ...(businessSize && { businessSize }),
  ...(businessRegion && { businessRegion }),
});

EventTracking.addOnBuyInteraction = (buyInteraction, buySelection, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType: `${flowType} add-ons`,
});

EventTracking.addTrialInteraction = (products, flowType = getTrackingFlowType()) => EventTracking({
  event: 'Product View',
  flowType,
  products
});

EventTracking.addressInteractionWBizInfo = (buySelection, checkbox, businessPersonalAccount,
  businessSize,
  businessRegion, flowType = getTrackingFlowType(),
) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout address',
    buySelection,
    flowType,
    ...(businessPersonalAccount && { businessPersonalAccount }),
    ...(businessSize && { businessSize }),
    ...(businessRegion && { businessRegion }),
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.addressInteraction = (buySelection, checkbox, flowType = getTrackingFlowType()) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout address',
    buySelection,
    flowType,
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.paymentInteraction = (buySelection, checkbox, flowType = getTrackingFlowType()) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout payment method',
    buySelection,
    flowType,
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.paymentInteractionWBiz = (buySelection, businessPersonalAccount,
  businessSize,
  businessRegion, flowType = getTrackingFlowType(),checkbox) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout payment method',
    buySelection,
    flowType,
    ...(businessPersonalAccount && { businessPersonalAccount }),
    ...(businessSize && { businessSize }),
    ...(businessRegion && { businessRegion }),
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.partnerLandingInteraction = (pricingSelection, uiLocation, pricingInteraction = gaFlowTypes.buyNow) => {
  let payload = {
    event: 'pricingInteraction',
    pricingInteraction,
    pricingSelection,
    uiLocation,
  }
  EventTracking(payload);
}

EventTracking.couponInteraction = (buySelection, couponCode, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction: 'order summary',
  buySelection,
  couponCode,
  flowType,
});

EventTracking.orderSummary = (buySelection) => basicFormatter('buyInteraction', 'order summary', buySelection)

EventTracking.sign = (accountSelection, accountMethod, modifier) => {
  let payload = {
    event: 'accountInteraction',
    accountInteraction: 'checkout sign up',
    accountSelection,
    flowType: gaFlowTypes[window.$context?.flow],
    accountMethod,
  };
  if (modifier) payload = Object.assign(payload, modifier(payload));
  EventTracking(payload);
}

EventTracking.paymentSwitchType = (paymentType,businessPersonalAccount,
  businessSize,
  businessRegion,) => EventTracking({
  event: 'Checkout Select Payment Type',
  paymentType,
  businessPersonalAccount,
  businessSize,
  businessRegion,
  flowType: getTrackingFlowType(),
});

EventTracking.postPurchase = (buySelection) => basicFormatter('buyInteraction', 'purchase confirmation', buySelection);

export const mapProducts = (plans, modifier = o => o) => {
  const flowType = getTrackingFlowType();

  return plans.map(x => {
    let brand = translation(x?.typeNameKey);
    if ([planTypes.ZOOM_PHONE$ZPA, planTypes.ZOOM_PHONE$ZPI].includes(x.type)) {
      brand = "Zoom Phone add-ons"
    }
    let product = {
      brand,
      category: flowType,
      name: translation(x?.planNameKey, [x?.capacity]),
      id: x?.plan_code,
      price: x?.amount / 100,
      list: flowType,
      quantity: x?.cartInfo?.selected_number,
    };
    // FREE TRIAL - different product data
    if(flowType === gaFlowTypes[flowTypes.add2trial]) {
      product = {
        ...product,
        brand: x?.type,
        category: x?.category,
        name: `${product?.brand || this.$t(x?.planNameKey)} Free Trial`, //zoom sessions free trial
        id: x?.isOnlineFreeTrial ? `${x?.plan_code}_free_trial`: x?.plan_code,
        price: product?.price,
      }
    }
    // BUY TRIAL - different product data
    if(flowType === gaFlowTypes[flowTypes.buyTrial]) {
      product = {
        ...product,
        brand: x?.type,
        category: x?.category,
        name: `${product?.brand || this.$t(x?.planNameKey)} Free Trial`, //scheduler free trial
        id: x?.isOnlineFreeTrial ? `${x?.plan_code}_free_trial`: x?.plan_code,
        price: product?.price,
        trialCode: x?.isOnlineFreeTrial ? x?.trialCode: ''
      }
    }
    return Object.assign(product, modifier(product, x));
  });
};

export default EventTracking;
